import { CoreLogger } from '../logger/core-logger';
import { ThemeStyleSheet } from './theme-style-sheet';

export class ThemeManager {
  private repository: Map<string, CSSStyleSheet> = new Map();
  private globalUrls: string[] = [];
  private applicationToUrlMap: Map<string, Set<string>> = new Map();
  //TODO theme change mechanism

  async addCSS(
    url: string | string[],
    global = false,
    microfrontendId: string = 'unknown',
  ) {
    const urls = this.prepareUrls(typeof url === 'string' ? [url] : url);
    if (microfrontendId === 'unknown') {
      CoreLogger.loggerManager.warn(
        'Use ScopedThemeManager from SDK, or pass microfrontendId explicitly',
      );
    }
    await Promise.all(
      urls.map((url) =>
        this.createAdoptedStylesheet(url, global, microfrontendId),
      ),
    );
    if (global && urls.length) {
      const newUrls = urls.filter((url) => !this.globalUrls.includes(url));
      this.globalUrls = [...this.globalUrls, ...urls];
      newUrls.forEach((u) => {
        const st = this.repository.get(u);
        if (st) {
          document.adoptedStyleSheets?.push(st);
        }
      });
    }
    return microfrontendId;
  }

  async addStyles(
    text: string,
    id: string = 'unknown',
    origin: string = window.location.origin,
  ) {
    if (this.repository.has(id)) {
      CoreLogger.loggerManager.info(`Stylesheet with id ${id} already exists.`);
      return id;
    }
    const styleSheet = await ThemeStyleSheet.createAdoptedStylesheet(
      text,
      origin,
      true,
    );
    this.repository.set(id, styleSheet);
    const idSet = new Set<string>([id]);
    this.applicationToUrlMap.set(id, idSet);
    return id;
  }

  getStyles(urls: string[], includeGlobals = true): CSSStyleSheet[] {
    return [...(includeGlobals ? this.globalUrls : []), ...urls]
      .map((url) => {
        return this.repository.get(url);
      })
      .filter((stylesheet): stylesheet is CSSStyleSheet => !!stylesheet);
  }

  getStylesByMicrofrontendId(
    ids: string | string[],
    includeGlobals = true,
  ): CSSStyleSheet[] {
    const idList = Array.isArray(ids) ? ids : [ids];
    const urlsSet = new Set<string>(includeGlobals ? this.globalUrls : []);
    idList.forEach((id) => {
      const urls = this.applicationToUrlMap.get(id) || new Set<string>();
      urls.forEach((url) => urlsSet.add(url));
    });
    return this.getStyles(Array.from(urlsSet), false);
  }

  setUrlGlobal(url: string, global = true) {
    if (!this.repository.has(url)) {
      if (url) {
        CoreLogger.loggerManager.info(`${url} not found.`);
      }
      return;
    }
    if (!global && this.globalUrls.includes(url)) {
      // remove from global list
      this.globalUrls = this.globalUrls.filter(
        (globalUrl) => globalUrl !== url,
      );
      return;
    }
    if (global && !this.globalUrls.includes(url)) {
      this.globalUrls.push(url);
    }
  }

  private prepareUrls(urls: string[]) {
    const uniqueStrings = [...new Set(urls)];
    return uniqueStrings.filter((url) => !(this.repository.has(url) || !url));
  }

  private async createAdoptedStylesheet(
    url: string,
    global = false,
    microfrontendId: string,
  ): Promise<CSSStyleSheet | undefined> {
    if (this.repository.has(url)) {
      CoreLogger.loggerManager.warn('CSS ', url, 'already has been added');
      return;
    }
    try {
      const response = await fetch(url);
      const cssText = await response.text();
      const resultStylesheet = await ThemeStyleSheet.createAdoptedStylesheet(
        cssText,
        url,
        global,
      );
      // consider that past fetching we are good and can store urls
      const urlSet =
        this.applicationToUrlMap.get(microfrontendId) || new Set<string>();
      urlSet.add(url);
      this.applicationToUrlMap.set(microfrontendId, urlSet);

      this.repository.set(url, resultStylesheet);
      return resultStylesheet;
    } catch (error) {
      CoreLogger.loggerManager.error('Error fetching stylesheet', error, url);
      return;
    }
  }
}
